import {useEffect,useId,useRef,useState} from 'react';
import {Link} from 'react-router-dom';
import './ClientInstallInstructions.css';
import {availableClientTools,installCommand,installPrompt,localInitPrompt,scriptInstallCommand} from './clientTools';
import ClientToolPicker from './ClientToolPicker';
import {CopyableCommand} from './CopyableCommand';
import {InstallDisclosure} from './InstallDisclosure';
import RemoteClientSetup from './RemoteClientSetup';
import {REMOTE_INIT_PROMPT} from './remoteInstallation';
import NativePluginSetup from './NativePluginSetup';

function LocalClientSteps({tool,config,planId}) {
  const [terminal,setTerminal]=useState(false);
  const [testMode,setTestMode]=useState(false);
  const [windows,setWindows]=useState(()=>/Win/i.test(navigator.platform));
  const desktop=tool.id==='claude-desktop';
  const useTerminal=terminal || desktop;
  const command=installCommand({client:tool.id,windows,testMode,planId});
  const npm=!testMode;
  const installText=useTerminal ? command : installPrompt(tool,command);
  return <ol className="pabs-brief-steps">
    <li className="pabs-brief-step" data-state="current">
      <span className="pabs-brief-step-number" aria-hidden="true">1</span>
      <div className="pabs-brief-step-body">
        <h3>{useTerminal ? 'Run this in your project folder to install Pabs' : `Open your project in ${tool.label} and paste this to install Pabs`}</h3>
        <div className="pabs-brief-step-content">
          <CopyableCommand key={installText} value={installText} label={useTerminal ? 'Copy install command' : 'Copy install prompt'}/>
          <details className="pabs-install-options">
            <summary>Setup options</summary>
            {npm ? <>
              <p>Node.js 18+ with npm is required. Run the same command again to update.</p>
              <p>No npm? Run <code>{scriptInstallCommand({client:tool.id,planId})}</code> instead, or <code>{scriptInstallCommand({client:tool.id,planId,windows:true})}</code> in Windows PowerShell.</p>
            </> : <p>Node.js 18+ and tar are required. Run the same command again to update.</p>}
            {!desktop && <button type="button" className="pabs-install-text-button" onClick={()=>setTerminal(!terminal)}>{terminal ? 'Use an install prompt instead' : 'Use a terminal instead'}</button>}
            {!npm && <div className="pabs-install-clients" role="group" aria-label="Terminal platform">
              <button type="button" aria-pressed={!windows} onClick={()=>setWindows(false)}>macOS / Linux</button>
              <button type="button" aria-pressed={windows} onClick={()=>setWindows(true)}>Windows PowerShell</button>
            </div>}
            {desktop && <p>Desktop connects to this project through MCP. Its tools can read project documents; coding and testing depend on your client’s other tools.</p>}
            {config.local_preview && <label className="pabs-install-test-option"><input type="checkbox" checked={testMode} onChange={event=>setTestMode(event.target.checked)}/> Include the temporary test reset skill</label>}
            {config.local_preview && <p>This preview connects to {window.location.origin}. Test reset requires the local test server.</p>}
          </details>
        </div>
      </div>
    </li>
    <li className="pabs-brief-step" data-state="current">
      <span className="pabs-brief-step-number" aria-hidden="true">2</span>
      <div className="pabs-brief-step-body">
        <h3>{desktop ? 'Restart Claude Desktop, then ask it to set up your project' : `Reload ${tool.label}, then ask it to set up your project`}</h3>
        <div className="pabs-brief-step-content">
          <CopyableCommand key={tool.id} value={localInitPrompt(tool.id)} label="Copy initialization prompt"/>
          <p>{tool.label} shows a Pabs approval link and a code. Open it, check the code matches and approve. Pabs then sets up your Brief and strategy from the project, or picks up a product you already have.</p>
        </div>
      </div>
    </li>
  </ol>;
}

// A tool already connected to this plan: its status and what to ask next,
// instead of install steps. Another project folder can still be set up.
function ConnectedStatus({tool,connection,planName,onInstallAnother}) {
  const since=new Date(connection.created_at).toLocaleDateString(undefined,{day:'numeric',month:'short'});
  const finishing=connection.status==='authorized';
  const prompt=finishing ? (tool.host ? REMOTE_INIT_PROMPT : localInitPrompt(tool.id)) : 'Use Pabs to help me choose the next thing to build.';
  return <div className="pabs-install-connected">
    <p className="pabs-install-connected-title">{tool.label} is connected to {planName || 'this plan'} since {since}.</p>
    <p>{finishing ? 'Its setup isn’t finished yet. Ask it:' : 'Ask it next:'}</p>
    <CopyableCommand key={prompt} value={prompt} label="Copy prompt"/>
    <p className="pabs-install-connected-links">
      <Link to="/connectors/ai-tools">Manage</Link>
      <span aria-hidden="true"> · </span>
      <button type="button" className="pabs-install-text-button" onClick={onInstallAnother}>{tool.host ? `Connect another ${tool.label} project` : 'Install in another project folder'}</button>
    </p>
  </div>;
}

// Signed-in /brief: pick the tool you build with, then follow its steps.
// From a plan's connection window it also gets the plan (planId, planName),
// the tools already connected to it, and optionally the tool to open on.
export default function BriefClientSetup({config,onDescribe,planId=null,planName=null,connectedClients=null,initialClient=null}) {
  const headingId=useId();
  const root=useRef(null);
  const requested=useRef(initialClient || new URLSearchParams(window.location.search).get('client'));
  const [choice,setChoice]=useState(requested.current);
  const [installAnother,setInstallAnother]=useState(false);
  const {tools}=availableClientTools(config);
  const connected=new Set((connectedClients || []).map(item=>item.client_name));
  // Without a requested tool, start on one that isn't connected yet.
  const fallback=tools.find(item=>!connected.has(item.id)) || tools[0];
  const tool=tools.find(item=>item.id===choice) || fallback;
  const select=id=>{setChoice(id);setInstallAnother(false);};
  const connection=(connectedClients || []).find(item=>item.client_name===tool?.id);
  const showConnected=Boolean(connection) && !installAnother;
  // The closing panel keeps its last tool while the other one opens.
  const lastLocal=useRef(null),lastRemote=useRef(null),lastNative=useRef(null);
  if(tool?.native) lastNative.current=tool; else if(tool?.host) lastRemote.current=tool; else if(tool) lastLocal.current=tool;
  useEffect(()=>{
    // Arriving from a tool's "Continue setup" link lands on that tool.
    if(!tools.some(item=>item.id===requested.current)) return;
    root.current?.scrollIntoView({block:'start'});
    requested.current=null;
  });
  if(!tool) return null;
  return <section ref={root} className="pabs-install pabs-brief-setup" aria-labelledby={headingId}>
    <div className="pabs-brief-setup-intro">
      <h2 id={headingId} className="pabs-brief-setup-title">Bring Pabs into your AI tool to guide what it builds</h2>
      {onDescribe && <p className="pabs-brief-setup-fallback">Can’t connect your tool? <button type="button" className="pabs-brief-link" onClick={onDescribe}>Describe your product instead</button></p>}
    </div>
    <ClientToolPicker tools={tools} selected={tool.id} onSelect={select} connected={connected}/>
    <hr className="pabs-brief-setup-rule"/>
    {showConnected ? <ConnectedStatus tool={tool} connection={connection} planName={planName} onInstallAnother={()=>setInstallAnother(true)}/> : <div className="pabs-brief-panels">
      <InstallDisclosure open={!!tool.native}>{lastNative.current && <NativePluginSetup tool={lastNative.current} planId={planId}/>}</InstallDisclosure>
      <InstallDisclosure open={!tool.host && !tool.native}>{lastLocal.current && <LocalClientSteps tool={lastLocal.current} config={config} planId={planId}/>}</InstallDisclosure>
      <InstallDisclosure open={!!tool.host}>{lastRemote.current && <RemoteClientSetup key={lastRemote.current.id} host={lastRemote.current.host} planId={planId}/>}</InstallDisclosure>
    </div>}
  </section>;
}
